/**
 * ChipTextField — text inputs with `/`-picker chip support.
 *
 * Two variants:
 *   - <ChipTextInput />   single-line, no Enter-submit, no hard breaks
 *   - <ChipTextArea />    multi-line, hard breaks allowed
 *
 * Both wrap the same TipTap-based ChatComposerEditor + SlashInlinePicker used
 * by the AI Chat surface, so `/` opens the Corpus picker and picks are inserted
 * as inline chip atoms (icon + label + inline × control).
 *
 * Persistence: parents pass `value` (plaintext with `@Label` mentions where
 * chips were) and receive `onChange(nextText)`. No schema changes required —
 * chips serialize to `@Label` via ChatComposerEditor.getPlainText().
 *
 * Rehydration tradeoff: on load, `@Label` mentions render as plaintext (no
 * chip atoms). Users can re-insert chips via `/` if they want the visual
 * treatment back. This keeps the field storage schema-free and makes template
 * seeding for the Planning Agent behave identically regardless of whether the
 * text was typed or picked.
 */
import { useEffect, useRef, useState } from "react";
import { ChatComposerEditor, type ChatComposerEditorHandle } from "./ChatComposerEditor";
import { SlashInlinePicker } from "./SlashInlinePicker";

type BaseProps = {
  workspaceId: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

const ChipField = ({
  workspaceId,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  minHeight,
  singleLine,
}: BaseProps & { minHeight: number; singleLine: boolean }) => {
  const editorRef = useRef<ChatComposerEditorHandle | null>(null);
  const [slashOpen, setSlashOpen] = useState(false);
  const initializedRef = useRef(false);
  const lastEmittedRef = useRef<string>("");

  // Initial content hydration (once per mount). Parents that need to fully
  // reset the field should remount by key.
  useEffect(() => {
    if (initializedRef.current) return;
    const editor = editorRef.current;
    if (!editor) return;
    initializedRef.current = true;
    editor.setPlainText(value ?? "");
    lastEmittedRef.current = value ?? "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => {
    const editor = editorRef.current;
    if (!editor) return;
    let next = editor.getPlainText();
    if (singleLine) next = next.replace(/\s+/g, " ").trimStart();
    if (next === lastEmittedRef.current) return;
    lastEmittedRef.current = next;
    onChange(next);
  };

  return (
    <div className={`relative ${className ?? ""}`}>
      <ChatComposerEditor
        ref={editorRef}
        placeholder={placeholder}
        disabled={disabled}
        minHeight={minHeight}
        onSubmit={singleLine ? () => { /* single-line disables Enter-submit */ } : undefined}
        onSlashTrigger={() => setSlashOpen(true)}
        onChange={() => emit()}
      />
      <div className="absolute left-2 top-2">
        <SlashInlinePicker
          workspaceId={workspaceId}
          open={slashOpen}
          onOpenChange={setSlashOpen}
          disabled={disabled}
          editorRef={editorRef}
        />
      </div>
    </div>
  );
};

export const ChipTextInput = (props: BaseProps) => (
  <ChipField {...props} minHeight={38} singleLine />
);

export const ChipTextArea = (props: BaseProps & { minHeight?: number }) => (
  <ChipField {...props} minHeight={props.minHeight ?? 72} singleLine={false} />
);
